/**
 * PERFORMANCE OPTIMIZED Product Video Player
 * Lazy loading and reduced initialization complexity
 */
class ProductVideoPlayer {
  constructor() {
    this.videoPlayers = {};
    this.carousels = new Map();
    this.currentSlide = new Map();
    this.slideCount = new Map();
    this.uploadedImages = new Map();
    this.observers = {};
    this.initializedPlayers = new Set();
    
    // PERFORMANCE: Delay initialization until user interaction or page load complete
    this.initializeWhenReady();
  }
  
  initializeWhenReady() {
    // Wait for page to be loaded before initializing
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.init());
    } else {
      // Use requestIdleCallback for better performance
      if (window.requestIdleCallback) {
        requestIdleCallback(() => this.init());
      } else {
        setTimeout(() => this.init(), 100);
      }
    }
  }
  
  init() {
    // PERFORMANCE: Only initialize visible video players
    const playerElements = document.querySelectorAll('[data-video-player]');
    
    // Use Intersection Observer for lazy initialization
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.initializePlayer(entry.target);
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.1 });
    
    playerElements.forEach(element => {
      // Check if in viewport immediately for above-fold content
      const rect = element.getBoundingClientRect();
      if (rect.top < window.innerHeight && rect.bottom > 0) {
        this.initializePlayer(element);
      } else {
        observer.observe(element);
      }
    });
  }
  
  initializePlayer(playerElement) {
      const playerId = playerElement.getAttribute('data-player-id') || `video-player-${Math.random().toString(36).substring(2, 9)}`;
      
      if (this.initializedPlayers.has(playerId)) {
        return;
      }
      
      playerElement.setAttribute('data-player-id', playerId);
      this.initializedPlayers.add(playerId);
      
    // Initialize components
      this.uploadedImages.set(playerId, []);
      this.setupImageUpload(playerId, playerElement);
      
      const slides = playerElement.querySelectorAll('.carousel-slide');
      if (slides.length > 0) {
        this.setupCarousel(playerId, playerElement);
      } else {
        this.initializeEmptyCarousel(playerId, playerElement);
      }
      
    // Setup video players with lazy loading
      const videoElements = playerElement.querySelectorAll('video');
      videoElements.forEach(video => {
        this.setupVideoPlayer(playerId, video);
    });
  }
  
  setupImageUpload(playerId, playerElement) {
    const uploadTrigger = playerElement.querySelector('[data-upload-trigger]');
    const uploadInput = playerElement.querySelector('[data-upload-input]');
    const uploadPreview = playerElement.querySelector('[data-upload-preview]');
    
    if (!uploadTrigger || !uploadInput || !uploadPreview) return;
    
    // Handle upload button click
    uploadTrigger.addEventListener('click', () => {
      uploadInput.click();
    });
    
    // Handle file selection
    uploadInput.addEventListener('change', (e) => {
      this.handleFileUpload(playerId, e.target.files, uploadPreview, playerElement);
    });
  }
  
  handleFileUpload(playerId, files, uploadPreview, playerElement) {
    const maxFiles = 5; // Limit to 5 uploaded images
    const currentUploaded = this.uploadedImages.get(playerId) || [];
    
    Array.from(files).slice(0, maxFiles - currentUploaded.length).forEach((file) => {
      if (file.type.startsWith('image/')) {
        // For better quality, use canvas to resize if image is too large
        this.processImageFile(file, (processedImageData) => {
          const imageData = {
            id: `upload-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            src: processedImageData,
            file: file,
            name: file.name
          };
          
          // Add to uploaded images array
          currentUploaded.push(imageData);
          this.uploadedImages.set(playerId, currentUploaded);
          
          // Update preview
          this.updateUploadPreview(playerId, uploadPreview);
          
          // Update carousel
          this.addUploadedImageToCarousel(playerId, imageData, playerElement);
          this.updateCarouselNavigation(playerId, playerElement);
        });
      }
    });
  }
  
  processImageFile(file, callback) {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        // Create canvas for high-quality processing
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        // Set high DPI canvas for crisp images
        const maxSize = 2048; // Maximum dimension
        const scale = Math.min(maxSize / img.width, maxSize / img.height, 1);
        
        canvas.width = img.width * scale;
        canvas.height = img.height * scale;
        
        // Enable high-quality image smoothing
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        
        // Draw image to canvas
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        
        // Convert to high-quality data URL
        const quality = 0.95; // High quality (0.0 to 1.0)
        const dataUrl = canvas.toDataURL('image/jpeg', quality);
        
        callback(dataUrl);
      };
      img.src = e.target.result;
    };
    
    reader.readAsDataURL(file);
  }
  
  updateUploadPreview(playerId, uploadPreview) {
    const uploadedImages = this.uploadedImages.get(playerId) || [];
    
    uploadPreview.innerHTML = uploadedImages.map(image => `
      <div class="upload-preview-item">
        <img src="${image.src}" alt="${image.name}">
        <button class="remove-upload" data-remove-upload="${image.id}">×</button>
      </div>
    `).join('');
    
    // Add remove functionality
    uploadPreview.querySelectorAll('[data-remove-upload]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const imageId = e.target.getAttribute('data-remove-upload');
        this.removeUploadedImage(playerId, imageId, uploadPreview);
      });
    });
  }
  
  removeUploadedImage(playerId, imageId, uploadPreview) {
    const uploadedImages = this.uploadedImages.get(playerId) || [];
    const filteredImages = uploadedImages.filter(img => img.id !== imageId);
    this.uploadedImages.set(playerId, filteredImages);
    
    // Update preview
    this.updateUploadPreview(playerId, uploadPreview);
    
    // Remove from carousel
    const playerElement = this.carousels.get(playerId);
    if (playerElement) {
      const slideToRemove = playerElement.querySelector(`[data-upload-id="${imageId}"]`);
      if (slideToRemove) {
        slideToRemove.remove();
        this.updateCarouselNavigation(playerId, playerElement);
        this.recalculateSlideIndices(playerId, playerElement);
      }
    }
  }
  
  addUploadedImageToCarousel(playerId, imageData, playerElement) {
    const track = playerElement.querySelector('[data-carousel-track]');
    if (!track) return;
    
    // Create slide element
    const slide = document.createElement('div');
    slide.className = 'carousel-slide';
    slide.setAttribute('data-slide-type', 'uploaded');
    slide.setAttribute('data-upload-id', imageData.id);
    slide.style.aspectRatio = '1/1';
    slide.style.overflow = 'hidden';
    
    slide.innerHTML = `
      <img src="${imageData.src}" 
           alt="${imageData.name}" 
           loading="lazy"
           style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; object-fit: cover; border-radius: 8px;">
    `;
    
    // Insert at the beginning (uploaded images first)
    track.insertBefore(slide, track.firstChild);
    
    // Update slide count and current slide
    const currentCount = this.slideCount.get(playerId) || 0;
    this.slideCount.set(playerId, currentCount + 1);
    
    // Update carousel track width and slide flex
    const totalSlides = this.slideCount.get(playerId);
    track.style.width = `${totalSlides * 100}%`;
    track.style.display = 'flex';
    track.style.transition = 'transform 0.3s ease';
    
    // Update each slide width
    const allSlides = track.querySelectorAll('.carousel-slide');
    allSlides.forEach(slideEl => {
      slideEl.style.flex = `0 0 ${100 / totalSlides}%`;
      slideEl.style.position = 'relative';
      slideEl.style.aspectRatio = '1/1';
      slideEl.style.overflow = 'hidden';
    });
    
    // Setup navigation buttons if they don't exist
    this.ensureNavigationButtons(playerId, playerElement);
    
    // Go to the newly added slide (first slide)
    this.currentSlide.set(playerId, 0);
    this.updateCarousel(playerId);
  }
  
  ensureNavigationButtons(playerId, playerElement) {
    // Check if navigation buttons exist, if not create them
    let prevBtn = playerElement.querySelector('[data-carousel-prev]');
    let nextBtn = playerElement.querySelector('[data-carousel-next]');
    const videoContainer = playerElement.querySelector('.video-container');
    
    if (!prevBtn && videoContainer) {
      prevBtn = document.createElement('button');
      prevBtn.className = 'carousel-nav prev';
      prevBtn.setAttribute('data-carousel-prev', '');
      prevBtn.innerHTML = `
        <svg viewBox="0 0 24 24">
          <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/>
        </svg>
      `;
      prevBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.previousSlide(playerId);
      });
      videoContainer.appendChild(prevBtn);
    }
    
    if (!nextBtn && videoContainer) {
      nextBtn = document.createElement('button');
      nextBtn.className = 'carousel-nav next';
      nextBtn.setAttribute('data-carousel-next', '');
      nextBtn.innerHTML = `
        <svg viewBox="0 0 24 24">
          <path d="M8.59 16.59L10 18l6-6-6-6-1.41 1.41L13.17 12z"/>
        </svg>
      `;
      nextBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.nextSlide(playerId);
      });
      videoContainer.appendChild(nextBtn);
    }
  }
  
  updateCarouselNavigation(playerId, playerElement) {
    const totalSlides = this.slideCount.get(playerId) || 0;
    const navButtons = playerElement.querySelectorAll('.carousel-nav');
    const dotsContainer = playerElement.querySelector('[data-carousel-dots]');
    
    // Show/hide navigation based on slide count
    if (totalSlides > 1) {
      navButtons.forEach(btn => btn.style.display = 'flex');
      if (dotsContainer) {
        dotsContainer.style.display = 'flex';
        this.regenerateDots(playerId, playerElement, totalSlides);
      }
    } else {
      navButtons.forEach(btn => btn.style.display = 'none');
      if (dotsContainer) dotsContainer.style.display = 'none';
    }
  }
  
  regenerateDots(playerId, playerElement, totalSlides) {
    const dotsContainer = playerElement.querySelector('[data-carousel-dots]');
    if (!dotsContainer) return;
    
    // Clear existing dots
    dotsContainer.innerHTML = '';
    
    // Create new dots
    for (let i = 0; i < totalSlides; i++) {
      const dot = document.createElement('button');
      dot.className = `carousel-dot ${i === 0 ? 'active' : ''}`;
      dot.setAttribute('data-dot-index', i);
      dot.setAttribute('aria-label', `Go to slide ${i + 1}`);
      
      dot.addEventListener('click', (e) => {
        e.preventDefault();
        this.goToSlide(playerId, i);
      });
      
      dotsContainer.appendChild(dot);
    }
  }
  
  recalculateSlideIndices(playerId, playerElement) {
    const slides = playerElement.querySelectorAll('.carousel-slide');
    this.slideCount.set(playerId, slides.length);
    
    // Update slide indices
    slides.forEach((slide, index) => {
      slide.setAttribute('data-slide-index', index);
    });
    
    // Update track width
    const track = playerElement.querySelector('[data-carousel-track]');
    if (track && slides.length > 0) {
      track.style.width = `${slides.length * 100}%`;
      slides.forEach(slide => {
        slide.style.flex = `0 0 ${100 / slides.length}%`;
      });
    }
    
    // Ensure current slide is within bounds
    const currentSlide = this.currentSlide.get(playerId) || 0;
    if (currentSlide >= slides.length) {
      this.currentSlide.set(playerId, Math.max(0, slides.length - 1));
    }
    
    this.updateCarousel(playerId);
  }
  
  setupCarousel(playerId, playerElement) {
    const track = playerElement.querySelector('[data-carousel-track]');
    if (!track) return;
    
    const slides = playerElement.querySelectorAll('.carousel-slide');
    const dots = playerElement.querySelectorAll('.carousel-dot');
    const prevBtn = playerElement.querySelector('[data-carousel-prev]');
    const nextBtn = playerElement.querySelector('[data-carousel-next]');
    
    // Store carousel data
    this.carousels.set(playerId, playerElement);
    this.slideCount.set(playerId, slides.length);
    this.currentSlide.set(playerId, 0);
    
    // Set up navigation buttons
    if (prevBtn) {
      prevBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.previousSlide(playerId);
      });
    }
    
    if (nextBtn) {
      nextBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.nextSlide(playerId);
      });
    }
    
    // Set up dots navigation
    dots.forEach((dot, index) => {
      dot.addEventListener('click', (e) => {
        e.preventDefault();
        this.goToSlide(playerId, index);
      });
    });
    
    // Touch/swipe support
    this.setupTouchNavigation(playerId, track);
    
    // Initialize first slide
    this.updateCarousel(playerId);
  }
  
  initializeEmptyCarousel(playerId, playerElement) {
    // Initialize carousel even with no slides
    const track = playerElement.querySelector('[data-carousel-track]');
    if (!track) return;
    
    this.carousels.set(playerId, playerElement);
    this.slideCount.set(playerId, 0);
    this.currentSlide.set(playerId, 0);
    
    // Set up touch navigation
    this.setupTouchNavigation(playerId, track);
    
    // Hide navigation initially
    const navButtons = playerElement.querySelectorAll('.carousel-nav');
    const dotsContainer = playerElement.querySelector('[data-carousel-dots]');
    navButtons.forEach(btn => btn.style.display = 'none');
    if (dotsContainer) dotsContainer.style.display = 'none';
  }
  
  setupVideoPlayer(playerId, video) {
    this.videoPlayers[`${playerId}-${video.dataset.slideIndex || 0}`] = video;
    
    // Handle replay if loop attribute is set
    if (video.hasAttribute('loop')) {
      video.addEventListener('ended', () => {
        setTimeout(() => {
          video.currentTime = 0;
          video.play().catch(err => console.log('Playback error:', err));
        }, 500);
      });
    }
    
    // Add loading indicator
    video.addEventListener('loadstart', () => {
      video.parentElement.classList.add('loading');
    });
    
    video.addEventListener('canplay', () => {
      video.parentElement.classList.remove('loading');
    });
  }
  
  goToSlide(playerId, slideIndex) {
    const slideCount = this.slideCount.get(playerId);
    if (slideIndex < 0 || slideIndex >= slideCount) return;
    
    this.currentSlide.set(playerId, slideIndex);
    this.updateCarousel(playerId);
  }
  
  nextSlide(playerId) {
    const currentIndex = this.currentSlide.get(playerId);
    const slideCount = this.slideCount.get(playerId);
    const nextIndex = (currentIndex + 1) % slideCount;
    this.goToSlide(playerId, nextIndex);
  }
  
  previousSlide(playerId) {
    const currentIndex = this.currentSlide.get(playerId);
    const slideCount = this.slideCount.get(playerId);
    const prevIndex = (currentIndex - 1 + slideCount) % slideCount;
    this.goToSlide(playerId, prevIndex);
  }
  
  updateCarousel(playerId) {
    const playerElement = this.carousels.get(playerId);
    const currentIndex = this.currentSlide.get(playerId);
    
    if (!playerElement) return;
    
    const track = playerElement.querySelector('[data-carousel-track]');
    const dots = playerElement.querySelectorAll('.carousel-dot');
    const mediaTitle = playerElement.querySelector('[data-media-title]');
    const slides = playerElement.querySelectorAll('.carousel-slide');
    
    // Update track position
    if (track) {
      const translateX = -currentIndex * (100 / this.slideCount.get(playerId));
      track.style.transform = `translateX(${translateX}%)`;
    }
    
    // Update dots
    dots.forEach((dot, index) => {
      dot.classList.toggle('active', index === currentIndex);
    });
    
    // Update media title if available
    if (mediaTitle && slides[currentIndex]) {
      const currentSlide = slides[currentIndex];
      const img = currentSlide.querySelector('img');
      const video = currentSlide.querySelector('video');
      
      if (img && img.alt) {
        mediaTitle.textContent = img.alt;
      } else if (video) {
        mediaTitle.textContent = 'Video';
      }
    }
    
    // Pause all videos except current one
    slides.forEach((slide, index) => {
      const video = slide.querySelector('video');
      if (video) {
        if (index !== currentIndex) {
          video.pause();
        }
      }
    });
  }
  
  setupTouchNavigation(playerId, track) {
    let startX = 0;
    let currentX = 0;
    let isDragging = false;
    
    const handleStart = (e) => {
      isDragging = true;
      startX = e.type === 'mousedown' ? e.clientX : e.touches[0].clientX;
      track.style.cursor = 'grabbing';
    };
    
    const handleMove = (e) => {
      if (!isDragging) return;
      e.preventDefault();
      currentX = e.type === 'mousemove' ? e.clientX : e.touches[0].clientX;
    };
    
    const handleEnd = () => {
      if (!isDragging) return;
      isDragging = false;
      track.style.cursor = 'grab';
      
      const diffX = startX - currentX;
      const threshold = 50;
      
      if (Math.abs(diffX) > threshold) {
        if (diffX > 0) {
          this.nextSlide(playerId);
        } else {
          this.previousSlide(playerId);
        }
      }
    };
    
    // Mouse events
    track.addEventListener('mousedown', handleStart);
    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleEnd);
    
    // Touch events
    track.addEventListener('touchstart', handleStart, { passive: true });
    track.addEventListener('touchmove', handleMove, { passive: false });
    track.addEventListener('touchend', handleEnd);
  }
}

// Global instance
let productVideoPlayerInstance = null;

// Initialize on DOM ready
document.addEventListener('DOMContentLoaded', function() {
  // Initialize the new carousel with upload functionality
  productVideoPlayerInstance = new ProductVideoPlayer();
  
  // Keep the old functionality for compatibility
  initProductVideoPlayers();
  
  // Re-initialize on Shopify section refresh if in editor
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', function(event) {
      if (productVideoPlayerInstance) {
        productVideoPlayerInstance.init();
      }
      initProductVideoPlayers();
    });
  }
});

function initProductVideoPlayers() {
  // Find all video players
  const videoPlayers = document.querySelectorAll('.product-video-player');
  
  if (!videoPlayers.length) return;
  
  videoPlayers.forEach(function(player) {
    const video = player.querySelector('video');
    
    // If no video element is found, exit early
    if (!video) return;
    
    // Handle loop functionality
    if (player.hasAttribute('data-replay') || video.hasAttribute('data-replay')) {
      video.addEventListener('ended', function() {
        setTimeout(() => {
          video.currentTime = 0;
          video.play().catch(err => console.log('Playback error:', err));
        }, 300);
      });
    }
    
    // Autoplay when visible
    if (video.hasAttribute('autoplay')) {
      // Create intersection observer
      const observer = new IntersectionObserver(function(entries) {
        entries.forEach(function(entry) {
          if (entry.isIntersecting) {
            video.play().catch(function(error) {
              console.log('Autoplay prevented. User interaction required.', error);
              // Try with muted if regular autoplay fails
              if (error.name === 'NotAllowedError') {
                video.muted = true;
                video.play().catch(e => console.log('Even muted autoplay failed:', e));
              }
            });
          } else {
            if (!video.paused) {
              video.pause();
            }
          }
        });
      }, {
        threshold: 0.3
      });
      
      // Start observing the video
      observer.observe(player);
    }
    
    // Make video clickable to play/pause
    player.addEventListener('click', function(e) {
      if (e.target !== video && !e.target.closest('.video-controls')) {
        if (video.paused) {
          video.play().catch(err => console.log('Play error:', err));
        } else {
          video.pause();
        }
      }
    });
    
    // Add loading indicator
    video.addEventListener('loadstart', function() {
      const container = video.closest('.video-container');
      if (container) {
        container.classList.add('loading');
      }
    });
    
    video.addEventListener('canplay', function() {
      const container = video.closest('.video-container');
      if (container) {
        container.classList.remove('loading');
      }
    });
  });
}

// Simple function to play video with error handling
function tryPlayVideo(video) {
  if (!video) return;
  
  const playPromise = video.play();
  
  if (playPromise !== undefined) {
    playPromise.catch(error => {
      // Auto-play was prevented
      console.log('Autoplay prevented. User interaction required.');
      // Add a play button overlay if needed
    });
  }
} 